/**
 * Service API — Paramétrage de l'organisation RH
 *
 * Wrapper asynchrone autour des endpoints backend `/rh/organisation/...`.
 * Endpoints (cf. spécification backend) :
 *   - GET    /rh/organisation                  → bundle complet OrganisationData
 *   - GET    /rh/organisation/directions       → Direction[]
 *   - POST   /rh/organisation/directions       → Direction
 *   - PATCH  /rh/organisation/directions/:id   → Direction
 *   - DELETE /rh/organisation/directions/:id   → void
 *   - Idem pour departements, services, categories, fonctions, postes, grilles-salaire
 *   - POST   /rh/organisation/import-localstorage  → { inserted, updated, skipped }
 *
 * Permissions :
 *   - Lecture : tout user authentifié
 *   - CRUD    : manage_organisation_rh / manage_postes_rh / manage_grilles_rh
 *
 * Ce service est consommé par `organisationService.ts` qui garde une API
 * synchrone (cache localStorage) et déclenche ces appels en arrière-plan.
 */

import axiosInstance from './axios.config';
import {
  OrganisationData,
  Direction,
  Departement,
  Service,
  Categorie,
  Fonction,
  Poste,
  GrilleSalaire,
} from '../types/organisation.types';

const BASE = '/rh/organisation';

export interface ImportLocalStorageSummary {
  inserted: number;
  updated: number;
  skipped: number;
}

class OrganisationApiService {
  // ── Bundle ──────────────────────────────────────────────────────────────

  async getBundle(): Promise<OrganisationData> {
    const r = await axiosInstance.get<OrganisationData>(`${BASE}`);
    return r.data;
  }

  // ── Directions ──────────────────────────────────────────────────────────

  async getDirections(): Promise<Direction[]> {
    const r = await axiosInstance.get<Direction[]>(`${BASE}/directions`);
    return r.data;
  }

  async createDirection(dto: Omit<Direction, 'id'> & { id?: string }): Promise<Direction> {
    const r = await axiosInstance.post<Direction>(`${BASE}/directions`, dto);
    return r.data;
  }

  async updateDirection(id: string, dto: Partial<Direction>): Promise<Direction> {
    const r = await axiosInstance.patch<Direction>(`${BASE}/directions/${id}`, dto);
    return r.data;
  }

  async deleteDirection(id: string): Promise<void> {
    await axiosInstance.delete(`${BASE}/directions/${id}`);
  }

  // ── Departements ────────────────────────────────────────────────────────

  async getDepartements(): Promise<Departement[]> {
    const r = await axiosInstance.get<Departement[]>(`${BASE}/departements`);
    return r.data;
  }

  async createDepartement(dto: Omit<Departement, 'id'> & { id?: string }): Promise<Departement> {
    const r = await axiosInstance.post<Departement>(`${BASE}/departements`, dto);
    return r.data;
  }

  async updateDepartement(id: string, dto: Partial<Departement>): Promise<Departement> {
    const r = await axiosInstance.patch<Departement>(`${BASE}/departements/${id}`, dto);
    return r.data;
  }

  async deleteDepartement(id: string): Promise<void> {
    await axiosInstance.delete(`${BASE}/departements/${id}`);
  }

  // ── Services ────────────────────────────────────────────────────────────

  async getServices(): Promise<Service[]> {
    const r = await axiosInstance.get<Service[]>(`${BASE}/services`);
    return r.data;
  }

  async createService(dto: Omit<Service, 'id'> & { id?: string }): Promise<Service> {
    const r = await axiosInstance.post<Service>(`${BASE}/services`, dto);
    return r.data;
  }

  async updateService(id: string, dto: Partial<Service>): Promise<Service> {
    const r = await axiosInstance.patch<Service>(`${BASE}/services/${id}`, dto);
    return r.data;
  }

  async deleteService(id: string): Promise<void> {
    await axiosInstance.delete(`${BASE}/services/${id}`);
  }

  // ── Categories ──────────────────────────────────────────────────────────

  async getCategories(): Promise<Categorie[]> {
    const r = await axiosInstance.get<Categorie[]>(`${BASE}/categories`);
    return r.data;
  }

  async createCategorie(dto: Omit<Categorie, 'id'> & { id?: string }): Promise<Categorie> {
    const r = await axiosInstance.post<Categorie>(`${BASE}/categories`, dto);
    return r.data;
  }

  async updateCategorie(id: string, dto: Partial<Categorie>): Promise<Categorie> {
    const r = await axiosInstance.patch<Categorie>(`${BASE}/categories/${id}`, dto);
    return r.data;
  }

  async deleteCategorie(id: string): Promise<void> {
    await axiosInstance.delete(`${BASE}/categories/${id}`);
  }

  // ── Fonctions ───────────────────────────────────────────────────────────

  async getFonctions(): Promise<Fonction[]> {
    const r = await axiosInstance.get<Fonction[]>(`${BASE}/fonctions`);
    return r.data;
  }

  async createFonction(dto: Omit<Fonction, 'id'> & { id?: string }): Promise<Fonction> {
    const r = await axiosInstance.post<Fonction>(`${BASE}/fonctions`, dto);
    return r.data;
  }

  async updateFonction(id: string, dto: Partial<Fonction>): Promise<Fonction> {
    const r = await axiosInstance.patch<Fonction>(`${BASE}/fonctions/${id}`, dto);
    return r.data;
  }

  async deleteFonction(id: string): Promise<void> {
    await axiosInstance.delete(`${BASE}/fonctions/${id}`);
  }

  // ── Postes ──────────────────────────────────────────────────────────────

  async getPostes(): Promise<Poste[]> {
    const r = await axiosInstance.get<Poste[]>(`${BASE}/postes`);
    return r.data;
  }

  async createPoste(dto: Omit<Poste, 'id'> & { id?: string }): Promise<Poste> {
    const r = await axiosInstance.post<Poste>(`${BASE}/postes`, dto);
    return r.data;
  }

  async updatePoste(id: string, dto: Partial<Poste>): Promise<Poste> {
    const r = await axiosInstance.patch<Poste>(`${BASE}/postes/${id}`, dto);
    return r.data;
  }

  async deletePoste(id: string): Promise<void> {
    await axiosInstance.delete(`${BASE}/postes/${id}`);
  }

  // ── Grilles de salaire ──────────────────────────────────────────────────

  async getGrillesSalaire(): Promise<GrilleSalaire[]> {
    const r = await axiosInstance.get<GrilleSalaire[]>(`${BASE}/grilles-salaire`);
    return r.data;
  }

  async createGrilleSalaire(dto: Omit<GrilleSalaire, 'id'> & { id?: string }): Promise<GrilleSalaire> {
    const r = await axiosInstance.post<GrilleSalaire>(`${BASE}/grilles-salaire`, dto);
    return r.data;
  }

  async updateGrilleSalaire(id: string, dto: Partial<GrilleSalaire>): Promise<GrilleSalaire> {
    const r = await axiosInstance.patch<GrilleSalaire>(`${BASE}/grilles-salaire/${id}`, dto);
    return r.data;
  }

  async deleteGrilleSalaire(id: string): Promise<void> {
    await axiosInstance.delete(`${BASE}/grilles-salaire/${id}`);
  }

  // ── Import bulk depuis localStorage ─────────────────────────────────────

  async importLocalStorage(data: OrganisationData): Promise<ImportLocalStorageSummary> {
    const r = await axiosInstance.post<ImportLocalStorageSummary>(
      `${BASE}/import-localstorage`,
      data,
    );
    return r.data;
  }
}

export const organisationApi = new OrganisationApiService();
export default organisationApi;
